import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../services/api';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import toast from 'react-hot-toast';

const ROLE_DESCRIPTIONS = {
  shop_worker: 'Δημιουργήστε νέες παραγγελίες από το μενού "Νέα Παραγγελία" και παρακολουθήστε την κατάστασή τους από "Οι Παραγγελίες μου".',
  factory_worker: 'Δείτε τις εισερχόμενες παραγγελίες του εργοστασίου σας από το μενού "Παραγγελίες Εργοστασίου".',
};

const STATUS_LABELS = {
  NEW: 'Νέες',
  IN_PROGRESS: 'Σε εξέλιξη',
  READY: 'Έτοιμες',
  DELIVERED: 'Παραδόθηκαν',
  CANCELLED: 'Ακυρώθηκαν',
};
const STATUS_ICON = {
  NEW: '🆕',
  IN_PROGRESS: '⚙️',
  READY: '✅',
  DELIVERED: '📦',
  CANCELLED: '❌',
};
const STATUS_COLOR = {
  NEW: { bg: 'var(--blue-light)', text: 'var(--blue-dark)' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e' },
  READY: { bg: '#dcfce7', text: 'var(--green-dark)' },
  DELIVERED: { bg: 'var(--gray-100)', text: 'var(--gray-600)' },
  CANCELLED: { bg: '#fee2e2', text: 'var(--red-dark)' },
};

export default function Home() {
  const { user } = useAuth();

  // Non-admin: keep the simple welcome message
  if (user?.role !== 'super_admin') {
    const desc = ROLE_DESCRIPTIONS[user?.role] || '';
    return (
      <>
        <div className="page-header">
          <h2>Καλώς ήρθατε{user?.full_name ? `, ${user.full_name}` : ''}!</h2>
          <p>Πίνακας ελέγχου συστήματος παραγγελιών</p>
        </div>
        <div className="page-body">
          <div className="card"><div className="card-body">
            <p style={{ margin: 0, color: 'var(--gray-600)', lineHeight: 1.6 }}>{desc}</p>
          </div></div>
        </div>
      </>
    );
  }

  return <AdminDashboard userName={user?.full_name} />;
}

function AdminDashboard({ userName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await getDashboard();
      setData(r.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh every 30s silently
  useEffect(() => {
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  const formatRelative = (iso) => {
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return 'μόλις τώρα';
    if (minutes < 60) return `πριν ${minutes}'`;
    const h = Math.round(minutes / 60);
    if (h < 24) return `πριν ${h} ώρ.`;
    return format(new Date(iso), 'd MMM, HH:mm', { locale: el });
  };

  const formatFullWhen = (iso) => format(new Date(iso), 'd MMM, HH:mm', { locale: el });

  const today = data?.today;
  const attention = data?.attention || [];

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>Καλώς ήρθατε{userName ? `, ${userName}` : ''}!</h2>
            <p>Πίνακας ελέγχου · {format(new Date(), "EEEE d MMMM yyyy", { locale: el })}</p>
          </div>
          <button className="btn btn-secondary" onClick={() => load()} title="Ανανέωση">↻</button>
        </div>
      </div>
      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : !data ? (
          <div className="card"><div className="card-body">Δεν υπάρχουν δεδομένα</div></div>
        ) : (
          <>
            {/* KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
              {['NEW', 'IN_PROGRESS', 'READY', 'DELIVERED'].map((s) => (
                <div key={s} style={{
                  background: 'white',
                  borderRadius: 10,
                  padding: '14px 16px',
                  border: '1px solid var(--gray-100)',
                  borderLeft: `4px solid ${STATUS_COLOR[s].text}`,
                }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                    {STATUS_ICON[s]} {STATUS_LABELS[s]}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: STATUS_COLOR[s].text, marginTop: 4 }}>
                    {today.status_counts[s] || 0}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals strip */}
            <div className="card" style={{ marginBottom: 18 }}>
              <div className="card-body" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'space-around' }}>
                <Metric label="Παραγγελίες σήμερα" value={today.total_orders} />
                <Metric label="Έσοδα σήμερα" value={`€${today.total_revenue.toFixed(2)}`} highlight />
                {today.status_counts.CANCELLED > 0 && (
                  <Metric label="Ακυρώσεις" value={today.status_counts.CANCELLED} muted />
                )}
              </div>
            </div>

            {/* Per-shop and per-factory breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 18 }}>
              <BreakdownTable title="🏪 Καταστήματα (σήμερα)" rows={today.revenue_by_shop} />
              <BreakdownTable
                title="🏭 Εργοστάσια (σήμερα)"
                rows={today.revenue_by_factory}
                note="Αξία των προϊόντων κάθε εργοστασίου. Μια παραγγελία με δύο εργοστάσια μετριέται και στα δύο, οπότε τα σύνολα εδώ δεν αθροίζονται στα έσοδα της ημέρας."
              />
            </div>

            {/* Attention list */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">⏰ Ενεργές παραγγελίες (παλαιότερες πρώτα)</span>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{attention.length} ενεργές</span>
              </div>
              <div className="card-body">
                {attention.length === 0 ? (
                  <div className="empty-state" style={{ padding: 20 }}>
                    <div>✨</div>
                    <p>Καμία ενεργή παραγγελία!</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {attention.map((o) => (
                      <div key={o.id}
                        onClick={() => navigate('/my-orders')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 12px',
                          borderRadius: 8,
                          background: 'var(--gray-50)',
                          cursor: 'pointer',
                          borderLeft: `3px solid ${STATUS_COLOR[o.status].text}`,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{STATUS_ICON[o.status]}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>
                            #{o.id} {o.customer_name || '(χωρίς όνομα)'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                            🏪 {o.shop_name} → 🏭 {o.factory_names || '—'}
                            {o.item_count > 0 && ` · ${o.ready_count}/${o.item_count} έτοιμα`}
                            {' · '}παραλαβή: {formatFullWhen(o.requested_for)}
                          </div>
                        </div>
                        <span className={'badge'} style={{
                          background: STATUS_COLOR[o.status].bg,
                          color: STATUS_COLOR[o.status].text,
                          fontSize: 11,
                        }}>{STATUS_LABELS[o.status]}</span>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', minWidth: 70, textAlign: 'right' }}>
                          {formatRelative(o.last_status_change_at)}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue-dark)', minWidth: 60, textAlign: 'right' }}>
                          €{parseFloat(o.grand_total).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Metric({ label, value, highlight, muted }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{label}</div>
      <div style={{
        fontSize: 22,
        fontWeight: 700,
        color: highlight ? 'var(--blue-dark)' : muted ? 'var(--gray-500)' : 'var(--gray-800)',
        marginTop: 4,
      }}>{value}</div>
    </div>
  );
}

function BreakdownTable({ title, rows, note }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
      </div>
      {note && (
        <div style={{ padding: '8px 12px 0', fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.4 }}>
          {note}
        </div>
      )}
      <div className="card-body" style={{ padding: 0 }}>
        {rows.length === 0 ? (
          <div style={{ padding: 14, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>—</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '8px 12px' }}>{r.name}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', color: 'var(--gray-500)' }}>
                    {r.order_count} {r.order_count === 1 ? 'παραγγ.' : 'παραγγ.'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--blue-dark)' }}>
                    €{parseFloat(r.total).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
